import { createServerFn } from "@tanstack/react-start";

import { authMiddleware, mondayBoards } from "@onesolution/middlewares";

import {
	BOARDS_KEY_MAPPER_ENUM,
	TASKS_COLUMNS,
	TASK_WHO,
} from "@onesolution/constants";
import { NOTIFICATIONS_COLUMNS } from "@onesolution/constants/monday/notifications";
import { PROJECT_COLUMNS } from "@onesolution/constants/monday/projects";
import type { MONDAY_TYPES } from "@onesolution/monday";
import { mondayClient } from "@onesolution/monday/monday";
import type { OperationMessage } from "@onesolution/monday/queries/operationDetails.graphql";
import { endOfDay, startOfDay } from "date-fns";
import z from "zod";
import {
	filterMessages,
	formatGlobalStatus,
	getStatusChoicesFromServer,
	wrapWithUseQuery,
} from "..";
import { getTimelineRange } from "./getOperationPlanningBase";

const getOperationsSubitemsSchema = z.object({
	itemIds: z.array(z.string()),
	depth: z.number(),
});

const handler = createServerFn()
	.middleware([authMiddleware, mondayBoards])
	.validator(getOperationsSubitemsSchema)
	.handler(async ({ data, context }) => {
		const notifs = await mondayClient.query.notifications(context, {
			onlyNotProcessed: true,
			columnIds: [NOTIFICATIONS_COLUMNS.pulseId],
		});

		if (data.depth === 0) {
			const [products, choices] = await Promise.all([
				mondayClient.query.items<
					MONDAY_TYPES["base"] & {
						subitems: { id: string }[];
						status: MONDAY_TYPES["status"];
						deadline: MONDAY_TYPES["date"];
						ziflow: MONDAY_TYPES["text"];
						ziflowStatus: MONDAY_TYPES["status"];
						timeline: MONDAY_TYPES["text"];
						statusGlobal: MONDAY_TYPES["text"];
						created_at: MONDAY_TYPES["date"];
					}
				>({
					itemIds: data.itemIds,
					columnIds: [
						PROJECT_COLUMNS.status,
						PROJECT_COLUMNS.deadline,
						PROJECT_COLUMNS.ziflow,
						PROJECT_COLUMNS.ziflowStatus,
						PROJECT_COLUMNS.statusGlobal,
						PROJECT_COLUMNS.timeline,
					],
					extraQuery: `
						created_at	
						subitems { id }
					`,
				}),
				getStatusChoicesFromServer({
					data: {
						boardId: BOARDS_KEY_MAPPER_ENUM.tasks,
						columnId: TASKS_COLUMNS.status,
					},
				}),
			]);

			return products
				.map((product) => {
					const timeline = getTimelineRange(product.timeline);

					return {
						id: product.id,
						name: product.name,
						status: product.status,
						assignees: [],
						order: new Date(product.created_at).getTime(),
						hasUnreadNotif: Boolean(
							notifs.find((n) => n.pulseId === product.id),
						),
						deadline: product.deadline,
						progress: {
							status: { label: "", color: "" },
							global: formatGlobalStatus(product.statusGlobal, choices),
						},
						services: [],
						tags: [],
						proof: { link: product.ziflow, status: product.ziflowStatus },
						...timeline,
						messagesCount: 0,
						subitemIds: product.subitems.map((s) => s.id),
					};
				})
				.sort((a, b) => a.order - b.order);
		}

		if (data.depth === 1) {
			const tasks = await mondayClient.query.items<
				MONDAY_TYPES["base"] & {
					status: MONDAY_TYPES["status"];
					deadline: MONDAY_TYPES["date"];
					timeline: MONDAY_TYPES["timeline"];
					user: { display_value: string; linked_item_ids: string[] };
					visibility: MONDAY_TYPES["checkbox"];
					type: MONDAY_TYPES["status"];
					who: MONDAY_TYPES["text"];
					created_at: MONDAY_TYPES["date"];
					updates: OperationMessage[];
				}
			>({
				itemIds: data?.itemIds,
				columnIds: [
					TASKS_COLUMNS.status,
					TASKS_COLUMNS.deadline,
					TASKS_COLUMNS.timeline,
					TASKS_COLUMNS.user,
					TASKS_COLUMNS.visibility,
					TASKS_COLUMNS.type,
				],
				extraQuery: `
				created_at
				updates(limit: 500) {
					text_body
					creator {
						name
					}
				}`,
			});

			return tasks
				.filter((task) => task.visibility)
				.map((task) => ({
					id: task.id,
					name: task.name,
					status: task.status,
					assignees: [],
					deadline: task.deadline,
					progress: { global: [], status: task.type },
					services: [],
					tags: [],
					order: new Date(task.created_at).getTime(),
					canEditStatus: task.who !== TASK_WHO.altavia,
					hasUnreadNotif: Boolean(notifs.find((n) => n.pulseId === task.id)),
					messagesCount: filterMessages(task.updates).length,
					startAt:
						task.timeline && startOfDay(task.timeline?.from).toISOString(),
					endAt: task.timeline && endOfDay(task.timeline?.to).toISOString(),
					proof: null,
					subitemIds: [],
				}))
				.sort((a, b) => a.order - b.order);
		}
	});

export const getOperationsSubitemsFromServer = wrapWithUseQuery(
	"operations-subitems",
	handler,
);
